/** Environmental impact tab - CO2e, water footprint, landfill volume, recycling/composting/AD
 * rates, food donation, avoidable vs unavoidable waste, and progress against sustainability
 * targets. Positioned as one view among several (not the sole focus) per the operational-first
 * design intent for this dashboard. */

import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { ENVIRONMENTAL_IMPACT } from '../data';
import { StatusChip } from './StatusChip';

const EnvironmentalTab = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const e = ENVIRONMENTAL_IMPACT;
  const onTrack = e.currentPerformancePercent >= e.sustainabilityTargetPercent;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardEnvironmentalTitle', 'Environmental impact')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardCo2eImpact', 'Estimated CO2e impact')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{(e.co2eImpactKg / 1000).toFixed(1)} tCO2e</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardWaterFootprint', 'Water footprint')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{(e.waterFootprintLitres / 1000).toLocaleString()} m\u00b3</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardLandfillVolume', 'Landfill volume')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{e.landfillVolumeKg.toLocaleString()} kg</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardFoodDonation', 'Food donation quantity')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{e.foodDonationKg.toLocaleString()} kg</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardDiversionRates', 'Diversion rates')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={6}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardRecyclingRate', 'Recycling rate')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{e.recyclingRatePercent.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardCompostingRate', 'Composting rate')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{e.compostingRatePercent.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('wasteDashboardAdRate', 'Anaerobic digestion rate')}</Typography>
              <Typography variant="textLG"
                fontWeight={600}>{e.anaerobicDigestionRatePercent.toFixed(1)}%</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Grid container
        spacing={6}>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('wasteDashboardAvoidableVsUnavoidable', 'Avoidable vs unavoidable waste')}
              </Typography>
              <Box sx={{ width: '100%', height: 24, borderRadius: '6px', display: 'flex', overflow: 'hidden' }}>
                <Box sx={{ width: `${e.avoidableWastePercent}%`, backgroundColor: theme.palette.error.main }} />
                <Box sx={{ width: `${e.unavoidableWastePercent}%`, backgroundColor: theme.palette.custom.gray[400] }} />
              </Box>
              <Box display="flex"
                justifyContent="space-between"
                sx={{ mt: 2 }}>
                <Typography variant="textSM">{t('wasteDashboardAvoidable', 'Avoidable')}: {e.avoidableWastePercent}%</Typography>
                <Typography variant="textSM">{t('wasteDashboardUnavoidable', 'Unavoidable')}: {e.unavoidableWastePercent}%</Typography>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('wasteDashboardSustainabilityTarget', 'Progress against sustainability targets')}
              </Typography>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}>
                <Typography variant="textSM">{t('wasteDashboardDiversionFromLandfill', 'Diversion from landfill')}</Typography>
                <StatusChip status={onTrack ? 'good' : 'warning'}
                  label={onTrack ? t('wasteDashboardOnTrack', 'On track') : t('wasteDashboardBehindTarget', 'Behind target')} />
              </Box>
              <Box sx={{ width: '100%', height: 12, borderRadius: '6px', backgroundColor: theme.palette.custom.gray[200], overflow: 'hidden' }}>
                <Box sx={{ width: `${e.currentPerformancePercent}%`, height: '100%', backgroundColor: onTrack ? theme.palette.success.main : theme.palette.warning.main }} />
              </Box>
              <Typography variant="textXS"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}>
                {t('wasteDashboardCurrentVsTarget', 'Current')}: {e.currentPerformancePercent.toFixed(1)}% {t('wasteDashboardTarget', 'Target')}: {e.sustainabilityTargetPercent}%
              </Typography>
            </Box>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnvironmentalTab;
